// ============================================================================
// Public admission form — field registry (OEL-138)
// ----------------------------------------------------------------------------
// The single source of truth for WHICH fields the public admission form can show
// and which of them a school is allowed to switch off.
//
// It lives in `shared` because three places have to agree on it exactly and would
// otherwise drift apart the first time a field is added:
//
//   • the public form          — decides what to render and what to validate
//   • the submit endpoint      — decides what to require and what to discard
//   • the Front CMS settings   — draws the toggles
//
// A key that is not in this registry cannot be switched off: the form renders it
// and the server treats it exactly as it did before, so adding a field to the form
// without touching this file is safe — it simply is not configurable yet.
// ============================================================================

/** The form's input stages, in order. The Review stage is not configurable. */
export const ADMISSION_STEPS = [
  'Student Info',
  'Birth & Identity',
  'Parent Info',
  'Address',
  'School & Course',
  'Documents',
] as const;

export interface AdmissionFieldSpec {
  /** Matches the key on the form payload, e.g. `father_name`. */
  key: string;
  /** What the applicant sees, reused as the toggle's label in settings. */
  label: string;
  /** Index into ADMISSION_STEPS. */
  step: number;
  /**
   * Cannot be switched off.
   *
   * Kept to the smallest set that would make an application unusable rather than
   * merely thinner: without a name and a phone number there is nobody to admit and
   * no way to reach them, and the duplicate check that stops one applicant
   * registering twice is keyed on exactly those two.
   */
  locked?: boolean;
  /**
   * This field cannot be filled unless its parent is on — a district list is empty
   * without a state, a combination list is empty without a course. Switching the
   * parent off therefore switches the child off too; see resolveAdmissionFieldConfig.
   */
  dependsOn?: string;
  /**
   * Required when the field is shown. Mirrors the validation that was already in
   * the form and in the submit endpoint; this registry does not invent new rules,
   * it only records which rule belongs to which key so that disabling the field
   * disables the rule with it.
   */
  requiredWhenEnabled?: boolean;
}

export const ADMISSION_FORM_FIELDS: AdmissionFieldSpec[] = [
  // ── Step 0: Student Info ──
  // "Application for" — 1st PU / 2nd PU / Long Term ... (OEL-190). The choices are
  // rows in admission_form_options (list_key 'application_for'); the picked label
  // is stored on online_admissions.registration_for. Not requiredWhenEnabled here:
  // the form only renders it once the school has added at least one option, and
  // the submit endpoint requires it exactly when a non-empty list exists.
  { key: 'application_for', label: 'Application for', step: 0 },
  // Which campus the application is for (OEL-231). Second question on the form,
  // straight after "Application for". Like that list it is drawn only once the
  // school has added options, and required exactly when it is drawn.
  { key: 'location', label: 'Select Location', step: 0 },
  { key: 'firstname', label: 'Student Full Name', step: 0, locked: true, requiredWhenEnabled: true },
  { key: 'gender', label: 'Gender', step: 0, requiredWhenEnabled: true },
  { key: 'dob', label: 'Date of Birth', step: 0, requiredWhenEnabled: true },
  { key: 'adhar_no', label: 'Aadhaar Number', step: 0, requiredWhenEnabled: true },
  { key: 'nationality', label: 'Nationality', step: 0 },
  { key: 'religion', label: 'Religion', step: 0, requiredWhenEnabled: true },
  { key: 'mother_tongue', label: 'Mother Tongue', step: 0 },
  { key: 'cast', label: 'Caste Name', step: 0 },
  { key: 'category_id', label: 'Cast Category', step: 0, requiredWhenEnabled: true },
  { key: 'blood_group', label: 'Blood Group', step: 0 },

  // ── Step 1: Birth & Identity ──
  { key: 'state_of_birth', label: 'State of Birth', step: 1, requiredWhenEnabled: true },
  { key: 'district_of_birth', label: 'District of Birth', step: 1, dependsOn: 'state_of_birth', requiredWhenEnabled: true },
  { key: 'place_of_birth', label: 'Place of Birth', step: 1, requiredWhenEnabled: true },
  { key: 'height', label: 'Height (cm)', step: 1 },
  { key: 'weight', label: 'Weight (kg)', step: 1 },
  { key: 'physically_challenged', label: 'Physically Challenged', step: 1 },
  { key: 'learning_disability', label: 'Learning Disability', step: 1 },
  { key: 'health_problems', label: 'Any Health Problems', step: 1 },

  // ── Step 2: Parent Info ──
  { key: 'father_name', label: "Father's Name", step: 2, requiredWhenEnabled: true },
  { key: 'mobileno', label: "Father's Phone (SMS/WhatsApp)", step: 2, locked: true, requiredWhenEnabled: true },
  { key: 'father_email', label: "Father's Email", step: 2 },
  { key: 'father_occupation', label: "Father's Occupation", step: 2 },
  { key: 'father_organization', label: "Father's Organisation", step: 2 },
  { key: 'mother_name', label: "Mother's Name", step: 2 },
  { key: 'alternate_number', label: "Mother's Phone (Alternate Number)", step: 2 },
  { key: 'mother_email', label: "Mother's Email", step: 2 },
  { key: 'mother_qualification', label: "Mother's Qualification", step: 2 },
  { key: 'mother_occupation', label: "Mother's Occupation", step: 2 },
  { key: 'mother_organization', label: "Mother's Organisation", step: 2 },
  { key: 'guardian_name', label: 'Emergency Contact — Name', step: 2 },
  { key: 'guardian_relation', label: 'Emergency Contact — Relationship', step: 2 },
  { key: 'guardian_phone', label: 'Emergency Contact — Mobile', step: 2 },
  { key: 'guardian_email', label: 'Emergency Contact — Email', step: 2 },

  // ── Step 3: Address ──
  { key: 'current_address', label: 'House No, Street Name', step: 3, requiredWhenEnabled: true },
  { key: 'city', label: 'Village / Area / City', step: 3 },
  { key: 'pincode', label: 'Pincode', step: 3, requiredWhenEnabled: true },
  { key: 'state', label: 'State', step: 3, requiredWhenEnabled: true },
  { key: 'address_district_id', label: 'District', step: 3, dependsOn: 'state', requiredWhenEnabled: true },
  { key: 'taluk', label: 'Taluk', step: 3, dependsOn: 'address_district_id' },
  { key: 'email', label: 'Student Email', step: 3 },

  // ── Step 4: School & Course ──
  { key: 'preferred_branch', label: 'Select Branch', step: 4 },
  { key: 'preferred_course', label: 'Select Course', step: 4 },
  { key: 'preferred_combination', label: 'Select Combination', step: 4, dependsOn: 'preferred_course' },
  { key: 'accommodation_preference', label: 'Accommodation Preference', step: 4 },
  { key: 'previous_school', label: '10th School Name', step: 4 },
  // Rendered as radio buttons from admission_form_options (list_key 'school_board')
  // when the school has added options; falls back to the built-in dropdown otherwise.
  { key: 'school_board', label: '10th Board', step: 4 },
  { key: 'school_marks', label: 'Total Marks', step: 4 },
  { key: 'school_percentage', label: 'Percentage / CGPA', step: 4 },
  { key: 'school_place', label: 'School Place', step: 4 },
  { key: 'source_of_info', label: 'How did you hear about us?', step: 4 },

  // ── Step 5: Documents ──
  { key: 'student_photo', label: 'Student Photo', step: 5, requiredWhenEnabled: true },
  { key: 'aadhaar_doc', label: 'Aadhaar Card', step: 5, requiredWhenEnabled: true },
];

/** Every configurable key, for seeding and for validating incoming settings. */
export const ADMISSION_FIELD_KEYS: string[] = ADMISSION_FORM_FIELDS.map((f) => f.key);

/**
 * The choice lists a school maintains in /students/online → Settings (OEL-190).
 * Rows live in `admission_form_options` keyed by `list_key`; the public form draws
 * each non-empty list as radio buttons. `column`/`maxLen` describe where the chosen
 * label ends up on online_admissions, so the settings screen can refuse a label the
 * column could not hold instead of letting the insert fail at submit time.
 */
export const ADMISSION_OPTION_LISTS = [
  { key: 'application_for', label: 'Application for', column: 'registration_for', maxLen: 100 },
  { key: 'school_board', label: '10th Board', column: 'school_board', maxLen: 50 },
  { key: 'location', label: 'Select Location', column: 'location', maxLen: 100 },
] as const;
export type AdmissionOptionListKey = (typeof ADMISSION_OPTION_LISTS)[number]['key'];
export const ADMISSION_OPTION_LIST_KEYS: string[] = ADMISSION_OPTION_LISTS.map((l) => l.key);
/** Shape delivered to the public form with the lookups: { application_for: [{id,label}], school_board: [...] } */
export type AdmissionFormOptions = Record<string, { id: number; label: string }[]>;

const SPEC_BY_KEY: Record<string, AdmissionFieldSpec> = ADMISSION_FORM_FIELDS
  .reduce((acc, f) => { acc[f.key] = f; return acc; }, {} as Record<string, AdmissionFieldSpec>);

export function admissionFieldSpec(key: string): AdmissionFieldSpec | undefined {
  return SPEC_BY_KEY[key];
}

/** key → shown?  Every registry key is always present, so callers never see undefined. */
export type AdmissionFieldConfig = Record<string, boolean>;

/**
 * Turn whatever is stored into the map the form and the submit endpoint use.
 *
 * Three corrections are applied here rather than at the call sites, so the client
 * and the server cannot reach different conclusions from the same stored rows:
 *
 *   1. a key absent from storage defaults to ON — a field added to the registry in
 *      a later release appears immediately instead of silently vanishing from every
 *      school's form until someone visits the settings page;
 *   2. a locked field is ON whatever is stored, so a stale or hand-edited row cannot
 *      produce an application with no name on it;
 *   3. a field whose parent is off is forced off, however it was stored. A district
 *      dropdown with no state to populate it is not a usable field, and leaving it
 *      on would strand the applicant on a required select they cannot satisfy.
 *      Applied repeatedly so a chain (state → district → taluk) collapses whole.
 */
export function resolveAdmissionFieldConfig(stored: Record<string, unknown> | null | undefined): AdmissionFieldConfig {
  const out: AdmissionFieldConfig = {};
  for (const f of ADMISSION_FORM_FIELDS) {
    const raw = stored ? stored[f.key] : undefined;
    const on = raw === undefined || raw === null ? true : !(raw === false || raw === 0 || raw === '0');
    out[f.key] = f.locked ? true : on;
  }
  // Collapse dependency chains. Bounded by the registry's depth; the loop exists
  // because state → district → taluk needs two passes and a third proves it settled.
  for (let pass = 0; pass < ADMISSION_FORM_FIELDS.length; pass++) {
    let changed = false;
    for (const f of ADMISSION_FORM_FIELDS) {
      if (f.dependsOn && out[f.key] && out[f.dependsOn] === false) { out[f.key] = false; changed = true; }
    }
    if (!changed) break;
  }
  return out;
}

/**
 * The stage order the school has arranged, as indices into ADMISSION_STEPS (OEL-232).
 *
 * Fail-open in every direction: nothing stored, unparseable JSON, a stage named
 * twice, an index that is not a stage — each is ignored, and any stage the stored
 * list forgets keeps its place at the end. The caller therefore always receives a
 * complete, duplicate-free permutation, and a school that has never touched the
 * order sees exactly the order it saw before this feature.
 */
export function resolveAdmissionStepOrder(raw: unknown): number[] {
  const natural = ADMISSION_STEPS.map((_, i) => i);
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return natural;
    try { parsed = JSON.parse(t); } catch { return natural; }
  }
  if (!Array.isArray(parsed)) return natural;
  const seen = new Set<number>();
  const out: number[] = [];
  for (const v of parsed) {
    const n = Number(v);
    if (!Number.isInteger(n) || n < 0 || n >= natural.length || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  for (const n of natural) if (!seen.has(n)) out.push(n);
  return out;
}

/** True when the order is simply the registry order — nothing worth storing. */
export function isNaturalAdmissionStepOrder(order: number[]): boolean {
  return order.length === ADMISSION_STEPS.length && order.every((n, i) => n === i);
}

/** The steps that still have at least one visible field. Empty steps are not shown. */
export function visibleAdmissionSteps(config: AdmissionFieldConfig): number[] {
  const steps: number[] = [];
  for (let i = 0; i < ADMISSION_STEPS.length; i++) {
    if (ADMISSION_FORM_FIELDS.some((f) => f.step === i && config[f.key])) steps.push(i);
  }
  return steps;
}

// ─── Per-field presentation (OEL-152) ────────────────────────────────────────
/**
 * What a school may change about a field without touching code: its label, a
 * help line under it, its position within its stage, and which courses it
 * applies to. Stored on admission_form_fields; absent = the registry default.
 */
export interface AdmissionFieldMeta {
  label?: string | null;
  help?: string | null;
  /** 1-based position within the field's stage; null = registry order. */
  order?: number | null;
  /** Course ids the field applies to; null / empty = every course. */
  courses?: number[] | null;
}
export type AdmissionFieldMetaMap = Record<string, AdmissionFieldMeta>;

/**
 * A course-restricted field is shown for that course — and, fail-open, while
 * no course has been chosen yet: most of the form comes BEFORE the course
 * picker, and hiding a field until a later stage is filled would make it
 * unreachable. The submit endpoint applies the same rule, so a value posted
 * for a field the chosen course does not use is discarded, not stored.
 */
export function fieldAppliesToCourse(meta: AdmissionFieldMeta | undefined | null, courseId: number | string | null | undefined): boolean {
  const list = meta?.courses;
  if (!list || list.length === 0) return true;
  const id = Number(courseId);
  if (!id) return true;
  return list.map(Number).includes(id);
}
